import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Clock, GraduationCap, TrendingUp, CheckCircle, ArrowRight, Users, Star, Award, ExternalLink, Globe, Zap, Target, ArrowLeft } from 'lucide-react';
import Footer from '../../components/landing/Footer';
import Config from '../../utils/Config';
import InteractiveSteps from '../../components/misc/InteractiveSteps';

const BecomeAnAgent = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  const handlePortalRedirect = () => {
    window.open(Config.AGENT_PORTAL_URL, '_blank');
  };

  const benefits = [
    {
      icon: DollarSign,
      title: "Earn Commission on Every Sale",
      description: "Get rewarded for every insurance policy, trip package, or service you sell through the TripSecure platform.",
      color: "bg-green-500"
    },
    {
      icon: Clock,
      title: "Work on Your Own Terms",
      description: "Whether full-time or part-time, you choose your schedule and earn from anywhere.",
      color: "bg-blue-500"
    },
    {
      icon: GraduationCap,
      title: "Access Training & Support",
      description: "We provide ongoing product training, digital tools, and marketing materials to help you succeed.",
      color: "bg-purple-500"
    },
    {
      icon: TrendingUp,
      title: "Be Part of a Growing Brand",
      description: "Join one of Africa's fastest-growing travel safety and insurance networks.",
      color: "bg-orange-500"
    }
  ];

  const agentBenefits = [
    "Competitive commission structure",
    "Access to exclusive travel deals",
    "Monthly recognition and reward programs",
    "Opportunities to grow into Regional or Senior Agent roles",
    "Invitations to TripSecure events and brand ambassador programs"
  ];

  const processSteps = [
    {
      title: "Register Online",
      description: "Fill out the simple online application form to become a verified TripSecure Agent",
      icon: Users,
      details: [
        "Complete the online application form",
        "Provide necessary documentation",
        "Wait for verification (24-48 hours)",
        "Receive welcome email with next steps"
      ]
    },
    {
      title: "Get Trained",
      description: "Attend our virtual onboarding and product training sessions",
      icon: GraduationCap,
      details: [
        "Attend virtual orientation session",
        "Complete product knowledge training",
        "Learn about commission structure",
        "Access marketing materials and tools"
      ]
    },
    {
      title: "Start Selling",
      description: "Access your unique agent dashboard, share TripSecure products, and start earning instantly",
      icon: DollarSign,
      details: [
        "Access your personalized agent dashboard",
        "Share TripSecure products with customers",
        "Track sales and commissions in real-time",
        "Receive instant commission notifications"
      ]
    },
    {
      title: "Track & Withdraw Earnings",
      description: "Monitor your sales in real time and withdraw your commissions with ease",
      icon: TrendingUp,
      details: [
        "Monitor sales performance dashboard",
        "Track commission earnings",
        "Withdraw funds to your preferred account",
        "Access detailed financial reports"
      ]
    }
  ];

  const whoCanBecome = [
    "Travel and tour consultants",
    "Insurance agents or brokers",
    "Safety and logistics professionals",
    "Students and freelancers",
    "Anyone passionate about helping others travel securely"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50">
      {/* Back Button */}
      <div className="fixed top-16 left-4 z-50">
        <motion.button
          onClick={handleBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Back</span>
        </motion.button>
      </div>
      {/* Hero Section */}
      <section className="relative pt-14 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-emerald-600/10" />
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl bg-blue-300/20" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full blur-3xl bg-emerald-300/20" />
        
        <div className="relative z-10 px-4 mx-auto max-w-6xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 border border-blue-200 mb-6">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Agent Network</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl lg:text-6xl mb-6">
              Join the TripSecure Network
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                Earn While Promoting Safer Travel
              </span>
            </h1>
            
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Are you passionate about travel, insurance, and customer service? Become a TripSecure Agent and start earning income by helping travellers stay protected, informed, and secure on every journey.
            </p>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 max-w-2xl mx-auto">
              <p className="text-sm text-gray-600">
                At TripSecure, we empower individuals and businesses to grow through our Pay-As-You-Go (PAYG) Travel Insurance, Tours & Smart Logistics, Safety Services, and Media & Events divisions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 mx-auto max-w-6xl sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Become a TripSecure Agent?</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            As an agent, you become a trusted link between TripSecure and our customers — ensuring that every traveller moves with peace of mind.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="group relative p-6 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
            >
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${benefit.color} mb-4`}>
                <benefit.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-sm text-gray-600">{benefit.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Who Can Become */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Who Can Become an Agent?</h2>
            <p className="text-lg text-gray-600">
              We welcome individuals from various backgrounds:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {whoCanBecome.map((person, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200"
              >
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-800">{person}</span>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              <strong>No experience? No problem!</strong>
            </p>
            <p className="text-sm text-gray-600">
              TripSecure provides free orientation and continuous support.
            </p>
          </div>
        </motion.div>

        {/* Interactive Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <InteractiveSteps
            title="Your Agent Journey"
            subtitle="Start earning as a TripSecure Agent in just four simple steps"
            steps={processSteps}
            theme="blue"
            autoPlay={true}
            autoPlayInterval={4000}
          />
        </motion.div>

        {/* Agent Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Agent Benefits at a Glance</h2>
            <p className="text-lg text-gray-600">
              Join our network and enjoy these exclusive benefits:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agentBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200"
              >
                <Star className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-800">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Portal Access */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Start Your Agent Journey</h2>
            <p className="text-lg text-gray-600 mb-6">
              Your journey to financial freedom and professional growth starts here.
            </p>
            <p className="text-sm text-gray-500">
              Ready to begin? Access our dedicated agent portal to start your application process.
            </p>
          </div>

          <div className="flex flex-col items-center space-y-6">
            <motion.button
              onClick={handlePortalRedirect}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="group flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <Globe className="w-5 h-5" />
              <span>Access Agent Portal</span>
              <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Agent Portal:</strong> {Config.AGENT_PORTAL_URL.replace('https://', '')}
              </p>
              <p className="text-xs text-gray-500">
                Click the button above to open the portal in a new tab
              </p>
            </div>
          </div>
        </motion.div>

        {/* Success Stories & Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-blue-600 to-emerald-600 rounded-2xl p-8 text-white mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-4">Join the Success Stories</h2>
            <p className="text-lg mb-6 opacity-90">
              Become part of the network that's redefining travel safety and insurance across Africa.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-3">
                <Target className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-1">500+</h3>
              <p className="text-sm opacity-90">Active Agents</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-3">
                <DollarSign className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-1">₵50K+</h3>
              <p className="text-sm opacity-90">Monthly Earnings</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-3">
                <Zap className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-1">Ghana</h3>
              <p className="text-sm opacity-90">Currently Operating</p>
            </div>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 rounded-full">
              <Star className="w-5 h-5" />
              <span className="text-sm font-medium">Join thousands of successful agents earning while promoting safer travel</span>
            </div>
          </div>
        </motion.div>

        {/* Final Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Your Journey Starts Here</h3>
            <p className="text-lg text-gray-600 mb-6">
              Join thousands of successful agents who are already earning while promoting safer travel across Africa. 
              Your financial freedom and professional growth await.
            </p>
            <div className="flex items-center justify-center gap-2 text-blue-600 font-semibold">
              <Award className="w-5 h-5" />
              <span>Start Your Agent Journey Today</span>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default BecomeAnAgent;
